import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import ErrorMessage from './ErrorMessage';
import { get, isChunkLoadError } from 'stash/utils';

const mapStateToProps = (state) => ({
	user_uuid: get(state, 'entities.user.uuid'),
});

// Guard key on sessionStorage so we don't loop reload if a chunk genuinely
// no longer exists on the server (e.g. very old tab against a new deploy
// where multiple chunk hashes have rolled). One reload attempt per session.
const CHUNK_RELOAD_GUARD_KEY = 'web_reg_chunk_reload_attempted';

// Module-scoped in-memory fallback. Used when sessionStorage is unavailable
// (Safari private mode, storage disabled, etc.) so that within a single page
// session repeated error-boundary triggers don't all try to reload. It does
// NOT survive a page reload — for that, we rely on a read-back check below.
const memoryStore = new Map();

const safeSessionStorage = {
	getItem(key) {
		try {
			const value = window.sessionStorage.getItem(key);
			if (value !== null) return value;
		} catch (_e) {
			// fall through to memory
		}
		return memoryStore.has(key) ? memoryStore.get(key) : null;
	},
	setItem(key, value) {
		memoryStore.set(key, value);
		try {
			window.sessionStorage.setItem(key, value);
		} catch (_e) {
			// no-op (Safari private mode, quota, etc.). Memory fallback above
			// keeps the guard intact for the remainder of this page session.
		}
	},
};

// Read only the persistent (sessionStorage) layer, bypassing the memory
// fallback. Used to verify that the guard will actually survive a reload
// before we trigger one — otherwise we'd loop forever.
const readPersistedGuard = (key) => {
	try {
		return window.sessionStorage.getItem(key);
	} catch (_e) {
		return null;
	}
};

// Test-only: the module-scoped memory fallback persists across Jest tests
// in the same worker. Exported so test setup can reset it between cases.
export const __resetChunkReloadGuardForTests = () => {
	memoryStore.clear();
};

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error) {
		if (isChunkLoadError(error)) {
			if (safeSessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1') {
				// Already reloaded once this session; the chunk is genuinely
				// missing or broken — fall through to the error UI rather
				// than loop.
				return;
			}
			safeSessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
			// Verify the guard actually persisted in sessionStorage. If the
			// write was swallowed (Safari private mode, quota, storage
			// disabled), reloading would loop forever because the guard
			// can't survive the reload. Surface the error UI instead.
			if (readPersistedGuard(CHUNK_RELOAD_GUARD_KEY) !== '1') {
				return;
			}
			return window.location.reload();
		}
	}

	render() {
		if (this.state.hasError) {
			return <ErrorMessage />;
		}

		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node,
	user_uuid: PropTypes.string,
	location: PropTypes.object,
};

export default connect(mapStateToProps)(ErrorBoundary);
